import { Link2, Link2Off } from "lucide-react"
import * as React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { cn } from "@/lib/utils"

import { PdfControls, PdfZoomControl } from "./pdf-controls"
import { FindingRibbon } from "./finding-ribbon"
import { findingPage, mapPage } from "./page-sync"
import { PdfSurface } from "./pdf-surface"
import { SplitPane } from "./split-pane"
import type { Finding, FindingId } from "../data/review-data"

/** Horizontal breathing room either side of a page inside its pane. */
const PANE_GUTTER = 48

/**
 * The narrowest a page is laid out at, however little room the pane has.
 *
 * Below this the label stops being legible and the reviewer is looking at a
 * thumbnail, which is a worse outcome than a page that overflows its pane and
 * scrolls.
 */
const MIN_BASE_WIDTH = 360

/**
 * One thing the right pane can show. `id` is opaque here — handed back on
 * change and resolved by the caller — so the pane stays presentational and a
 * third kind of comparison needs no change in it.
 */
export interface CompareOption {
  id: string
  /** What the selector shows: `Governing master`, `Previous version (v3)`. */
  label: string
  /** The pane's own header, naming the document rather than the choice. */
  paneLabel: string
  paneSubtitle: string
  paneProvenance: string
  documentUrl: string
  pageCount: number
}

export interface SideBySideProps {
  fplUrl: string
  fplLabel: string
  /** Identity shown in each pane's header band beside the filename. */
  fplProvenance: string
  /** A placeholder of 1 until the backend publishes `documents.page_count`;
   *  the paginator hides itself at 1. */
  fplPageCount: number
  page: number
  onPageChange: (page: number) => void
  zoom: number
  onZoomChange: (zoom: number) => void
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelectFinding: (id: FindingId) => void
  /** Never empty: the master is always one of them. */
  compareOptions: readonly [CompareOption, ...CompareOption[]]
  compareId: string
  onCompareChange: (id: string) => void
}

/**
 * The printed label and the master it is judged against, side by side.
 *
 * Each pane owns its own page and zoom. The two documents are different
 * lengths and different layouts — a master is often a multi-page template
 * where the FPL is one panel — so a shared page number would scroll one pane
 * to a page that does not correspond to anything, and a shared zoom would make
 * the denser document unreadable at the size that suits the other. What *is*
 * shared is the finding under discussion: both panes carry the same navigator
 * so stepping through findings moves the conversation, not the viewports.
 *
 * Only the FPL carries marks. Findings record their occurrences on the printed
 * label (`side: "fpl"`), so there is nothing to draw on the master until the
 * pipeline emits master-side occurrences.
 */
export function SideBySide({
  fplUrl,
  fplLabel,
  fplProvenance,
  fplPageCount,
  page,
  onPageChange,
  zoom,
  onZoomChange,
  findings,
  activeFindingId,
  onSelectFinding,
  compareOptions,
  compareId,
  onCompareChange,
}: SideBySideProps): JSX.Element {
  // The master's own page and zoom. Kept here rather than in the parent: it is
  // a property of this view, and leaving the compare screen should not leave a
  // stale master page behind for the next time it is opened.
  const [masterPage, setMasterPage] = React.useState(1)
  // Real lengths arrive from pdf.js, not the API. The right-hand count is keyed
  // by option because the selector swaps the document under it.
  const [fplPages, setFplPages] = React.useState(fplPageCount)
  const [comparePages, setComparePages] = React.useState<Record<string, number>>({})
  const [masterZoom, setMasterZoom] = React.useState(1)
  // A selection can outlive its option when the thread changes; the master is
  // always first and never wrong to fall back to.
  const selected = compareOptions.find((o) => o.id === compareId) ?? compareOptions[0]

  // A swapped-in document may be shorter than the page the pane sat on.
  React.useEffect(() => {
    setMasterPage(1)
  }, [selected.id])

  const [showBoxes, setShowBoxes] = React.useState(true)

  // Off by default: a pane that moves on its own is startling until asked for.
  // While on, a page turn on either side moves the other (see `mapPage`).
  const [locked, setLocked] = React.useState(false)
  const compareCount = comparePages[selected.id] ?? selected.pageCount

  const changeFplPage = React.useCallback(
    (next: number) => {
      onPageChange(next)
      if (locked) setMasterPage(mapPage(next, fplPages, compareCount))
    },
    [locked, onPageChange, fplPages, compareCount],
  )

  const changeComparePage = React.useCallback(
    (next: number) => {
      setMasterPage(next)
      if (locked) onPageChange(mapPage(next, compareCount, fplPages))
    },
    [locked, onPageChange, fplPages, compareCount],
  )

  // While locked, the compare pane follows the selected finding's page. Keyed on
  // the finding, not the page, so re-selecting it after paging away returns.
  React.useEffect(() => {
    if (!locked || activeFindingId === null) return
    const active = findings.find((f) => f.id === activeFindingId)
    const target = active === undefined ? null : findingPage(active)
    if (target !== null) setMasterPage(mapPage(target, fplPages, compareCount))
    // Counts excluded on purpose: one arriving later must not re-jump a reader who moved on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFindingId, locked, findings])

  // Locking is itself a move: a lock that waits for the next page turn looks broken.
  const toggleLock = (): void => {
    if (!locked) setMasterPage(mapPage(page, fplPages, compareCount))
    setLocked(!locked)
  }

  // Both panes render a page at the same width, derived from the compare area
  // rather than hardcoded: at zoom 1 a page should fill the space it is given,
  // and a fixed number is either a thumbnail on a wide screen or an overflow on
  // a narrow one. Measured on the whole area and halved, not per pane, so the
  // two documents stay the same size as the divider moves — a size difference
  // the reviewer did not ask for reads as a difference in the documents.
  const areaRef = React.useRef<HTMLDivElement>(null)
  const [areaWidth, setAreaWidth] = React.useState(0)
  React.useEffect(() => {
    const el = areaRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const measure = (): void => {
      const next = Math.round(el.getBoundingClientRect().width)
      setAreaWidth((current) => (current === next ? current : next))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => {
      ro.disconnect()
    }
  }, [])

  const baseWidth = Math.max(MIN_BASE_WIDTH, Math.round(areaWidth / 2) - PANE_GUTTER)

  return (
    <div ref={areaRef} className="h-full min-h-0">
      {/* Above both panes: inside the right one it would sit under that pane's
          floating navigator. Right-aligned to read against the pane it changes;
          omitted at one option, since a choice with no alternative is noise. */}
      {compareOptions.length > 1 && (
        <div className="flex shrink-0 items-center justify-end gap-2 border-b border-border-soft px-4 py-2">
          <button
            type="button"
            aria-pressed={locked}
            onClick={toggleLock}
            title={
              locked
                ? "Scrolling is linked — turning a page moves both documents"
                : "Link scrolling so both documents turn together"
            }
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-md border px-2",
              "font-id text-[10px] uppercase tracking-[0.1em]",
              "transition-colors duration-120 ease-out",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
              // Bordered even when off: borderless beside a label reads as more label.
              locked
                ? "border-brand bg-brand-tint text-brand"
                : "border-border bg-surface text-text-2 hover:border-border-strong hover:bg-surface-sunken hover:text-text",
            )}
          >
            {locked ? (
              <Link2 className="size-3.5" aria-hidden="true" />
            ) : (
              <Link2Off className="size-3.5" aria-hidden="true" />
            )}
            Sync
          </button>
          <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
            Compare against
          </span>
          <Select value={selected.id} onValueChange={onCompareChange}>
            <SelectTrigger className="h-7 w-[15rem] text-caption">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {compareOptions.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <SplitPane
        storageKey="review:side-by-side"
        defaultFraction={0.5}
        minFraction={0.3}
        maxFraction={0.7}
        ariaLabel="Resize compare panes"
        left={
          <ComparePane
            documentUrl={fplUrl}
            label={fplLabel}
            subtitle="Final Printed Label"
            provenance={fplProvenance}
            page={page}
            pageCount={fplPages}
            onPageCount={setFplPages}
            onPageChange={changeFplPage}
            zoom={zoom}
            onZoomChange={onZoomChange}
            findings={findings}
            activeFindingId={activeFindingId}
            onSelectFinding={onSelectFinding}
            baseWidth={baseWidth}
            showBoxes={showBoxes}
            onToggleBoxes={() => {
              setShowBoxes((v) => !v)
            }}
          />
        }
        right={
          <ComparePane
            documentUrl={selected.documentUrl}
            label={selected.paneLabel}
            subtitle={selected.paneSubtitle}
            provenance={selected.paneProvenance}
            page={masterPage}
            pageCount={comparePages[selected.id] ?? selected.pageCount}
            onPageCount={(count) => {
              setComparePages((prev) => ({ ...prev, [selected.id]: count }))
            }}
            onPageChange={changeComparePage}
            zoom={masterZoom}
            onZoomChange={setMasterZoom}
            findings={findings}
            activeFindingId={activeFindingId}
            onSelectFinding={onSelectFinding}
            baseWidth={baseWidth}
            // No marks on the master: see the note on this component.
            showBoxes={false}
          />
        }
      />
    </div>
  )
}

/**
 * One document in the compare view: the page, and the controls that drive it.
 *
 * The controls sit inside the pane rather than above the split so each one
 * reports its own page and zoom, and so the reviewer's hand stays over the
 * document it is steering.
 */
function ComparePane({
  documentUrl,
  label,
  subtitle,
  provenance,
  page,
  pageCount,
  onPageCount,
  onPageChange,
  zoom,
  onZoomChange,
  findings,
  activeFindingId,
  onSelectFinding,
  baseWidth,
  showBoxes,
  onToggleBoxes,
}: {
  documentUrl: string
  label: string
  subtitle: string
  provenance: string
  page: number
  pageCount: number
  onPageCount: (count: number) => void
  onPageChange: (page: number) => void
  zoom: number
  onZoomChange: (zoom: number) => void
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelectFinding: (id: FindingId) => void
  baseWidth: number
  showBoxes: boolean
  onToggleBoxes?: () => void
}): JSX.Element {
  return (
    // `isolate` keeps the floating controls' z-index local to this pane. Without
    // it they compete with the whole application and paint over a modal that is
    // meant to be covering the workspace.
    <div className="relative isolate h-full min-h-0">
      <PdfSurface
        documentUrl={documentUrl}
        label={label}
        subtitle={subtitle}
        provenance={provenance}
        page={page}
        onPageCount={onPageCount}
        zoom={zoom}
        onZoomChange={onZoomChange}
        findings={findings}
        activeFindingId={activeFindingId}
        onSelectFinding={onSelectFinding}
        baseWidth={baseWidth}
        showBoxes={showBoxes}
      />
      {/* Below the header band rather than over it: the band names the document
          and the controls steer it, so they stack in that order instead of
          competing for the same strip. */}
      {/* Navigator above, zoom below: the finding under discussion is what the
          reviewer steps through while reading, so it sits at the top edge where
          the eye already is; magnification is occasional and lives out of the
          way. Separating them also keeps each pill narrow enough to float over
          a half-width pane without being clipped. */}
      {/* A mark like the boxes, so it follows the same rule: findings carry FPL
          page numbers, and placing them on the master's ribbon mixes two
          documents' pages on one ruler. */}
      {showBoxes && (
        <FindingRibbon
          findings={findings}
          pageCount={pageCount}
          page={page}
          onSelectFinding={onSelectFinding}
        />
      )}
      <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-4">
        <PdfControls
          findings={findings}
          activeFindingId={activeFindingId}
          onSelectFinding={onSelectFinding}
          page={page}
          pageCount={pageCount}
          onPageChange={onPageChange}
          showBoxes={showBoxes}
          compact
          {...(onToggleBoxes ? { onToggleBoxes } : {})}
        />
      </div>
      <div className="pointer-events-none absolute bottom-4 right-4 z-10 flex justify-end">
        <PdfZoomControl zoom={zoom} onZoomChange={onZoomChange} />
      </div>
    </div>
  )
}
